import React, { useState, useEffect } from "react";
import {
  Header,
  HeaderContainer,
  SkipToContent,
  HeaderName,
  HeaderMenu,
  HeaderMenuItem,
  HeaderNavigation,
  HeaderGlobalBar,
  HeaderGlobalAction,
  Tag,
  HeaderMenuButton,
  SideNav,
  SideNavItems,
  SideNavLink,
} from "@carbon/react";
import "./header.scss";
import { useTranslation } from "react-i18next";
import { Logout } from "@carbon/icons-react";
import logo from "../../assets/icons/LogoNZT.svg";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { queryClient } from "../..";
import { useFuelRequestsStore } from "../../store/fuelRequestStore";
import { getRoute, clearRoute } from "../../store/routeStorage";

const languages = [
  { id: "en", lang: "ENG" },
  { id: "zh", lang: "中文" },
  { id: "pt", lang: "POR" },
  { id: "ru", lang: "РУС" },
];

export default function AgentHeader() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleRootClick = (e) => {
    const lastRoute = getRoute("admin");

    if (!lastRoute) return;

    const isLastRouteCurrent = lastRoute.pathname === location.pathname;

    if (isLastRouteCurrent) {
      e.preventDefault();

      clearRoute("admin");

      navigate("/", {
        replace: true,
        state: { skipRedirect: true },
      });
    }
  };

  const [currentLanguage, setCurrentLanguage] = useState("");

  const [activeNavItem, setActiveNavItem] = useState("/");

  const { fuelRequestsCount } = useFuelRequestsStore();

  useEffect(() => {
    setActiveNavItem(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    switch (i18n.language.slice(0, 2)) {
      case "en":
        setCurrentLanguage("ENG");
        break;
      case "zh":
        setCurrentLanguage("中文");
        break;
      case "pt":
        setCurrentLanguage("POR");
        break;
      case "ru":
        setCurrentLanguage("РУС");
        break;
      default:
        break;
    }
  }, [i18n.language]);

  const redirectToLogin = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userLogin");
    queryClient.clear();

    navigate("/login");
  };

  return (
    <HeaderContainer
      render={({ isSideNavExpanded, onClickSideNavExpand }) => (
        <Header aria-label="NZT">
          <SkipToContent />

          <HeaderMenuButton
            aria-label="Open menu"
            onClick={onClickSideNavExpand}
            isActive={isSideNavExpanded}
          />
          <HeaderName prefix="">
            <img src={logo} alt="" />
            <span>{t("documentTitle")}</span>
          </HeaderName>
          <HeaderNavigation aria-label="NZT">
            <Link
              to={`/`}
              className={`navLink ${
                activeNavItem === "/" ? "header__menu-item-active" : ""
              }`}
              onClick={handleRootClick}
            >
              {t("main.header.admin")}
            </Link>

            <Link
              to={`/fuel_requests`}
              className={`navLink ${
                activeNavItem.includes("/fuel_requests")
                  ? "header__menu-item-active"
                  : ""
              }`}
            >
              {t("main.header.fuel.requests")}
              {fuelRequestsCount ? (
                <Tag className="requests_count" size="md" title="" type="blue">
                  {fuelRequestsCount}
                </Tag>
              ) : null}
            </Link>
            <Link
              to={`/fuel_history`}
              className={`navLink ${
                activeNavItem.includes("/fuel_history")
                  ? "header__menu-item-active"
                  : ""
              }`}
            >
              {t("main.header.fuel.history")}
            </Link>
            <Link
              to={`/institute`}
              className={`navLink ${
                activeNavItem.includes("/institute")
                  ? "header__menu-item-active"
                  : ""
              }`}
            >
              {t("main.header.institute")}
            </Link>
          </HeaderNavigation>
          <HeaderGlobalBar>
            <HeaderMenu
              aria-label=""
              menuLinkName={currentLanguage}
              align="bottom-right"
            >
              {languages.map((item) => (
                <HeaderMenuItem
                  onClick={() => i18n.changeLanguage(item.id)}
                  key={item.id}
                >
                  {item.lang}
                </HeaderMenuItem>
              ))}
            </HeaderMenu>
            <HeaderGlobalAction
              aria-label={t("main.header.exit")}
              tooltipAlignment="center"
              onClick={() => redirectToLogin()}
            >
              <Logout size={20} />
            </HeaderGlobalAction>
          </HeaderGlobalBar>

          <SideNav
            aria-label="Side navigation"
            expanded={isSideNavExpanded}
            onOverlayClick={onClickSideNavExpand}
            isPersistent={false}
            isRail
          >
            <SideNavItems>
              <SideNavLink
                as={Link}
                to="/"
                isActive={activeNavItem === "/"}
                onClick={() => {
                  onClickSideNavExpand();
                  handleRootClick();
                }}
              >
                {t("main.header.admin")}
              </SideNavLink>
              <SideNavLink
                as={Link}
                to="/fuel_requests"
                isActive={activeNavItem === "/fuel_requests"}
                onClick={onClickSideNavExpand}
              >
                {t("main.header.fuel.requests")}
              </SideNavLink>
              <SideNavLink
                as={Link}
                to="/fuel_history"
                isActive={activeNavItem === "/fuel_history"}
                onClick={onClickSideNavExpand}
              >
                {t("main.header.fuel.history")}
              </SideNavLink>
              <SideNavLink
                as={Link}
                to="/institute"
                isActive={activeNavItem.includes("/institute")}
                onClick={onClickSideNavExpand}
              >
                {t("main.header.institute")}
              </SideNavLink>
            </SideNavItems>
          </SideNav>
        </Header>
      )}
    />
  );
}
